// src/components/Notifications/LiveOpenToasts.js
//
// Polls for classifier-confirmed human opens and raises a NATIVE OS
// notification for each new one (Web Notifications API -- works in Firefox/
// Chrome over HTTPS while this tab is open, no extension needed).
//
// Notifications are TIERED by engagement (session_number from the backend,
// same collapsing math as the Dashboard hot panel):
//   1st session ........ routine "just read your email"
//   2nd session ........ "opened again" (warming up)
//   3rd+ (is_hot) ...... hot-prospect banner; requireInteraction keeps it
//                        on screen until dismissed (Chrome; other browsers
//                        fall back to normal behavior)
//
// The in-page toast stack is intentionally not rendered; this component runs
// headless and only produces desktop banners. Restore the JSX at the bottom if
// you ever want the in-app cards back.
import { useEffect, useRef, useCallback } from 'react';

const POLL_INTERVAL_MS = 20000;   // 20s
const LOOKBACK_MINUTES = 30;      // must exceed the poller's classify cycle

// 1 -> '1st', 2 -> '2nd', 3 -> '3rd', 4 -> '4th', 11 -> '11th', 22 -> '22nd'
function ordinal(n) {
  const rem10 = n % 10;
  const rem100 = n % 100;
  if (rem10 === 1 && rem100 !== 11) return `${n}st`;
  if (rem10 === 2 && rem100 !== 12) return `${n}nd`;
  if (rem10 === 3 && rem100 !== 13) return `${n}rd`;
  return `${n}th`;
}

export default function LiveOpenToasts() {
  const seenIds = useRef(new Set());
  // '${campaign_id}:${email}' -> highest session number already announced.
  // A session can produce several pixel fires (scrolling re-renders); each is
  // a distinct open id, so id-dedupe alone would fire several banners for one
  // reading session. Announce once per session, and again only when the
  // session number ADVANCES.
  const lastSessionNotified = useRef(new Map());
  const seeded = useRef(false);
  const permission = useRef(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );

  // Ask for OS-notification permission once on mount.
  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      Notification.requestPermission().then((p) => { permission.current = p; });
    } else {
      permission.current = Notification.permission;
    }
  }, []);

  const notify = useCallback((open) => {
    if (permission.current !== 'granted') return;
    try {
      const sessions = open.session_number || 1;

      let title;
      if (open.is_hot) {
        title = `🔥 ${open.email} — ${ordinal(sessions)} read`;
      } else if (sessions === 2) {
        title = `👀 ${open.email} opened again`;
      } else {
        title = `📧 ${open.email} just read your email`;
      }

      const parts = [];
      if (open.company) parts.push(open.company);
      if (open.is_hot) parts.push('Hot prospect');
      parts.push(formatTime(open.opened_at));
      const body = parts.join(' — ');

      // tag = open id: dedupes at the OS level too, so a re-poll can't double-fire.
      // requireInteraction: hot banners persist until dismissed (Chrome).
      new Notification(title, {
        body,
        tag: `open-${open.id}`,
        requireInteraction: Boolean(open.is_hot),
      });
    } catch (e) {
      // Notification construction can throw if permission was revoked mid-session.
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const res = await fetch(
        `/api/notifications/recent-opens/?minutes=${LOOKBACK_MINUTES}`,
        { credentials: 'include' }
      );
      if (!res.ok) return;
      const data = await res.json();
      const opens = data.opens || [];

      // First poll after load: remember existing opens WITHOUT notifying,
      // so we only surface opens that arrive from here forward.
      if (!seeded.current) {
        opens.forEach((o) => {
          seenIds.current.add(o.id);
          const key = `${o.campaign_id}:${o.email}`;
          const sessions = o.session_number || 1;
          const prev = lastSessionNotified.current.get(key) || 0;
          if (sessions > prev) lastSessionNotified.current.set(key, sessions);
        });
        seeded.current = true;
        return;
      }

      opens
        .filter((o) => !seenIds.current.has(o.id))
        .forEach((o) => {
          seenIds.current.add(o.id);
          const key = `${o.campaign_id}:${o.email}`;
          const sessions = o.session_number || 1;
          const prev = lastSessionNotified.current.get(key) || 0;
          if (sessions <= prev) return; // this session already announced
          lastSessionNotified.current.set(key, sessions);
          notify(o);
        });
    } catch (e) {
      // transient network error -- retry next interval
    }
  }, [notify]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [poll]);

  // Headless: no in-page UI. (To restore in-app cards, render a stack here.)
  return null;
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}